/**
 * Painel Berytus › Catálogo mestre: o que falta nas fichas, leituras da CMED e da ABCFarma, busca e a ficha completa
 * (origem de cada campo, preços por alíquota, clientes ligados) com correção à mão.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Search } from 'lucide-react';
import { PHARMA_FORMS, REGULATORY_LABEL } from '@berytus/core';
import { api, brl, dateTime, int } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { Head, Pager, useSearchBox, useUrlState } from './MeliChannel.tsx';

interface Summary {
  totals: { products: number; pharmacy: number; complete: number; hospital: number; controlled: number; priced: number; linked: number; clients: number };
  missing: { key: string; label: string; pharmaOnly?: boolean; count: number }[];
}
interface Run { id: number; source: string; status: string; file: string | null; stats: Record<string, number>; error: string | null; startedAt: string; finishedAt: string | null }
interface Row { id: string; title: string; brand: string | null; manufacturer: string | null; vertical: string; gtin: string | null; image: string | null; missing: string[]; hospitalOnly: boolean; controlled: boolean; clients: number; updatedAt: string }
type Src = { source: string; level: number; at: string };
type Master = Record<string, any> & { masterId: string; title: string; gtins: string[]; sources: Record<string, Src>; missing: string[];
  prices: { source: string; kind: 'pf' | 'pmc'; icms: number; alc: boolean; value: number; validFrom: string | null }[];
  clients: { tenant: string; sku: string; title: string | null }[] };

const SOURCE: Record<string, string> = { cmed: 'CMED', abcfarma: 'ABCFarma' };
const MISSING: Record<string, string> = {
  brand: 'marca', manufacturer: 'fabricante', images: 'foto', weight: 'peso bruto', dimensions: 'medidas', ncm: 'NCM', category: 'categoria',
  description: 'descrição', anvisa: 'registro Anvisa', classification: 'classificação', form: 'forma farmacêutica', mip: 'MIP/tarja', pmc: 'PMC',
};
const FLAG: [string, string][] = [['', 'Todas'], ['hospital', 'Restrição hospitalar'], ['controlled', 'Controlados'], ['no_sales_cmed', 'Sem comercialização (CMED)'], ['discontinued', 'Descontinuados'], ['not_pharmacy', 'Não medicamentos']];
const srcLabel = (s: Src | undefined) => {
  if (!s) return null;
  const [kind, name] = s.source.split(':');
  const who = kind === 'admin' ? `Painel (${name})` : kind === 'official' || kind === 'reference' ? SOURCE[name ?? ''] ?? name : name;
  return `${who}${s.level === 1 ? ' · só preencheu' : ''}`;
};

export function AdminCatalog() {
  const isSuper = useMe().user.platformRole === 'superadmin';
  const qc = useQueryClient();
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const missing = params.get('falta') ?? '';
  const flag = params.get('marcador') ?? '';
  const page = Number(params.get('page') ?? 1);
  const summary = useQuery({ queryKey: ['admin', 'catalog', 'summary'], queryFn: () => api<Summary>('/admin/catalog/summary') });
  const sources = useQuery({
    queryKey: ['admin', 'catalog', 'sources'], queryFn: () => api<{ runs: Run[] }>('/admin/catalog/sources'),
    refetchInterval: (q) => (q.state.data?.runs.some((r) => r.status === 'running') ? 5000 : false),
  });
  const qs = new URLSearchParams({ page: String(page), pageSize: '50', ...(params.get('q') ? { q: params.get('q')! } : {}), ...(missing ? { missing } : {}), ...(flag ? { flag } : {}) });
  const list = useQuery({ queryKey: ['admin', 'catalog', 'list', qs.toString()], queryFn: () => api<{ total: number; page: number; pageSize: number; items: Row[] }>(`/admin/catalog/products?${qs}`), placeholderData: keepPreviousData });
  const run = useMutation({
    mutationFn: (v: { source: string; force?: boolean }) => api(`/admin/catalog/sources/${v.source}/run`, { method: 'POST', json: { force: !!v.force } }),
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ['admin', 'catalog'] }), 1500),
  });
  const s = summary.data;
  const labelOf = useMemo(() => new Map((s?.missing ?? []).map((m) => [m.key, m.label])), [s]);
  const last = (src: string) => sources.data?.runs.find((r) => r.source === src);

  return (
    <>
      <Head title="Catálogo mestre">
        <p>As fichas da Berytus por EAN, que alimentam o cadastro de todos os clientes. A CMED é lida sozinha quando sai planilha nova (conferida todo dia às 6h) e a ABCFarma toda segunda às 5h. Correção feita aqui vale acima de qualquer fonte.</p>
      </Head>
      <ShopeeSuggestions isSuper={isSuper} />
      {summary.error && <ErrorBox error={summary.error} />}
      {s && (
        <div className="kpis">
          <div className="kpi"><span className="k-label">Fichas</span><span className="k-value">{int(s.totals.products)}</span><span className="k-note">{int(s.totals.pharmacy)} medicamentos</span></div>
          <div className="kpi"><span className="k-label">Completas</span><span className="k-value">{int(s.totals.complete)}</span><span className="k-note">{s.totals.products ? `${Math.round((s.totals.complete / s.totals.products) * 100)}%` : '—'} com tudo o que é essencial</span></div>
          <div className="kpi"><span className="k-label">Com preço (PF/PMC)</span><span className="k-value">{int(s.totals.priced)}</span><span className="k-note">CMED e ABCFarma</span></div>
          <div className="kpi"><span className="k-label">Em uso</span><span className="k-value">{int(s.totals.linked)}</span><span className="k-note">fichas ligadas a {int(s.totals.clients)} cliente(s)</span></div>
        </div>
      )}

      <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 'var(--gap)', margin: 'var(--gap) 0' }}>
        <section className="panel stack-y" style={{ gap: 8 }}>
          <h2 style={{ margin: 0 }}>O que falta</h2>
          <span className="small muted">Clique para ver as fichas sem o item.</span>
          {!s ? <Loading /> : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {s.missing.map((m) => (
                <button key={m.key} type="button" className="btn sm" aria-pressed={missing === m.key} style={missing === m.key ? { borderColor: 'var(--brand)' } : undefined}
                  onClick={() => set({ falta: missing === m.key ? null : m.key, page: null })}>
                  {m.label} <b style={{ color: m.count ? 'var(--warn)' : 'var(--ok)' }}>{int(m.count)}</b>
                </button>
              ))}
            </div>
          )}
        </section>
        <section className="panel stack-y" style={{ gap: 8 }}>
          <h2 style={{ margin: 0 }}>Fontes</h2>
          {sources.error && <ErrorBox error={sources.error} />}
          {(['cmed', 'abcfarma'] as const).map((src) => {
            const r = last(src);
            return (
              <div key={src} style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div className="small" style={{ minWidth: 0 }}>
                  <b>{SOURCE[src]}</b>{' '}
                  {!r ? <span className="muted">nunca lida</span> : (
                    <>
                      <Tag tone={r.status === 'succeeded' ? 'ok' : r.status === 'running' ? 'info' : 'err'}>{r.status === 'succeeded' ? 'ok' : r.status === 'running' ? 'lendo…' : 'falhou'}</Tag>{' '}
                      <span className="muted">{dateTime(r.startedAt)}</span>
                      {r.status === 'succeeded' && <div className="muted">{int(r.stats.withEan ?? 0)} com EAN · {int(r.stats.created ?? 0)} novas · {int(r.stats.updated ?? 0)} atualizadas · {int(r.stats.prices ?? 0)} preços{r.stats.conflicts ? ` · ${int(r.stats.conflicts)} conflitos` : ''}</div>}
                      {r.error && <div style={{ color: 'var(--err)' }}>{r.error}</div>}
                    </>
                  )}
                </div>
                {isSuper && <button className="btn sm" type="button" disabled={run.isPending || r?.status === 'running'} onClick={() => run.mutate({ source: src, force: src === 'cmed' })}><RefreshCw size={13} /> Ler agora</button>}
              </div>
            );
          })}
          {run.error && <ErrorBox error={run.error} />}
          {run.isSuccess && <span className="small muted">Pedido enviado ao worker: a leitura aparece aqui em instantes.</span>}
        </section>
      </div>

      <div className="toolbar" style={{ flexWrap: 'wrap' }}>
        <label className="search">
          <Search size={16} /><span className="sr-only">Buscar</span>
          <input className="input" type="search" placeholder="EAN, registro Anvisa, nome, marca ou fabricante" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <select className="select" aria-label="Marcador" value={flag} onChange={(e) => set({ marcador: e.target.value || null, page: null })}>
          {FLAG.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {missing && <button className="btn sm quiet" type="button" onClick={() => set({ falta: null, page: null })}>Sem o filtro "{labelOf.get(missing) ?? missing}" ✕</button>}
      </div>
      <div className="panel flush" style={{ opacity: list.isPlaceholderData ? 0.6 : 1 }}>
        {list.error ? <div style={{ padding: 18 }}><ErrorBox error={list.error} /></div> : !list.data ? <Loading /> : !list.data.items.length ? (
          <div className="empty"><b>Nenhuma ficha</b>Nada com esse filtro.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="simple">
              <thead><tr><th>Ficha</th><th>EAN</th><th>Falta</th><th className="num">Clientes</th><th>Atualizada</th></tr></thead>
              <tbody>
                {list.data.items.map((r) => (
                  <tr key={r.id}>
                    <td style={{ maxWidth: 420 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {r.image ? <img src={r.image} alt="" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 6, background: '#fff' }} /> : <span style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--hover)', flexShrink: 0 }} />}
                        <div style={{ minWidth: 0 }}>
                          <Link to={`/admin/catalogo/${r.id}`} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.title}>{r.title}</Link>
                          <div className="small muted">{[r.brand, r.manufacturer].filter(Boolean).join(' · ') || '—'}{r.hospitalOnly && <> · <span style={{ color: 'var(--err)' }}>hospitalar</span></>}{r.controlled && <> · <span style={{ color: 'var(--err)' }}>controlado</span></>}</div>
                        </div>
                      </div>
                    </td>
                    <td className="small code">{r.gtin ?? '—'}</td>
                    <td style={{ maxWidth: 320 }}>{r.missing.length ? <span className="small" style={{ color: 'var(--warn)' }}>{r.missing.map((m) => labelOf.get(m) ?? m).join(', ')}</span> : <Tag tone="ok">completa</Tag>}</td>
                    <td className="num">{int(r.clients)}</td>
                    <td className="small muted">{dateTime(r.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list.data && <Pager total={list.data.total} page={page} pageSize={list.data.pageSize} onPage={(n) => set({ page: n > 1 ? String(n) : null })} />}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Ficha

type Kind = 'text' | 'area' | 'number' | 'bool' | 'select' | 'lines' | 'ingredients';
interface FieldDef { path: string; label: string; kind?: Kind; options?: [string, string][]; wide?: boolean }
const yesNo: [string, string][] = [['', 'Não informado'], ['true', 'Sim'], ['false', 'Não']];
const GROUPS: { title: string; pharma?: boolean; fields: FieldDef[] }[] = [
  { title: 'Identificação', fields: [
    { path: 'title', label: 'Título', wide: true }, { path: 'brand', label: 'Marca' }, { path: 'manufacturer', label: 'Fabricante' },
    { path: 'manufacturerCnpj', label: 'CNPJ do fabricante' }, { path: 'line', label: 'Linha' },
    { path: 'vertical', label: 'Tipo', kind: 'select', options: [['pharmacy', 'Farmácia (medicamento/saúde)'], ['general', 'Geral']] },
    { path: 'department', label: 'Departamento' }, { path: 'category', label: 'Categoria' }, { path: 'subcategory', label: 'Subcategoria' },
  ] },
  { title: 'Textos', fields: [
    { path: 'shortDescription', label: 'Descrição curta', kind: 'area', wide: true }, { path: 'description', label: 'Descrição', kind: 'area', wide: true },
    { path: 'usage', label: 'Modo de usar', kind: 'area', wide: true }, { path: 'warnings', label: 'Advertências', kind: 'area', wide: true },
    { path: 'ingredients', label: 'Composição', kind: 'area', wide: true },
  ] },
  { title: 'Embalagem e frete', fields: [
    { path: 'content.value', label: 'Conteúdo', kind: 'number' },
    { path: 'content.unit', label: 'Unidade', kind: 'select', options: [['', '—'], ...['ml', 'l', 'g', 'kg', 'mg', 'mcg', 'un', 'cm', 'm', 'ui'].map((u) => [u, u] as [string, string])] },
    { path: 'packUnits', label: 'Unidades na embalagem', kind: 'number' },
    { path: 'dimensions.heightCm', label: 'Altura (cm)', kind: 'number' }, { path: 'dimensions.widthCm', label: 'Largura (cm)', kind: 'number' }, { path: 'dimensions.lengthCm', label: 'Comprimento (cm)', kind: 'number' },
    { path: 'netWeightKg', label: 'Peso líquido (kg)', kind: 'number' }, { path: 'grossWeightKg', label: 'Peso bruto (kg)', kind: 'number' },
  ] },
  { title: 'Fiscal', fields: [
    { path: 'fiscal.ncm', label: 'NCM' }, { path: 'fiscal.cest', label: 'CEST' },
    { path: 'fiscal.origin', label: 'Origem', kind: 'select', options: [['', '—'], ...[0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => [String(n), String(n)] as [string, string])] },
  ] },
  { title: 'Medicamento', pharma: true, fields: [
    { path: 'pharma.anvisaCode', label: 'Registro / notificação Anvisa' },
    { path: 'pharma.regulatoryCategory', label: 'Classificação do medicamento', kind: 'select', options: [['', '—'], ...Object.entries(REGULATORY_LABEL)] },
    { path: 'pharma.pharmaceuticalForm', label: 'Forma farmacêutica', kind: 'select', options: [['', '—'], ...PHARMA_FORMS.map((f) => [f, f] as [string, string])] },
    { path: 'pharma.activeIngredients', label: 'Princípios ativos (um por linha: nome | concentração)', kind: 'ingredients', wide: true },
    { path: 'pharma.presentation', label: 'Apresentação', wide: true }, { path: 'pharma.dosage', label: 'Dosagem' }, { path: 'pharma.route', label: 'Via' },
    { path: 'pharma.therapeuticClass', label: 'Classe terapêutica', wide: true }, { path: 'pharma.atcCode', label: 'ATC' }, { path: 'pharma.dcb', label: 'DCB' },
    { path: 'pharma.referenceProduct', label: 'Medicamento de referência' },
    { path: 'pharma.tarja', label: 'Tarja', kind: 'select', options: [['', '—'], ['livre', 'Venda livre (MIP)'], ['vermelha', 'Vermelha'], ['vermelha_retencao', 'Vermelha com retenção'], ['preta', 'Preta']] },
    { path: 'pharma.controlledList', label: 'Lista Portaria 344', kind: 'select', options: [['', '—'], ...['A1', 'A2', 'A3', 'B1', 'B2', 'C1', 'C2', 'C3', 'C4', 'C5', 'D1', 'D2'].map((l) => [l, l] as [string, string])] },
    { path: 'pharma.isMedicine', label: 'É medicamento', kind: 'bool' }, { path: 'pharma.mip', label: 'MIP', kind: 'bool' },
    { path: 'pharma.controlled', label: 'Controlado', kind: 'bool' }, { path: 'pharma.psychotropic', label: 'Psicotrópico', kind: 'bool' },
    { path: 'pharma.antibiotic', label: 'Antibiótico', kind: 'bool' }, { path: 'pharma.thermolabile', label: 'Refrigerado', kind: 'bool' },
    { path: 'pharma.blister', label: 'Blister', kind: 'bool' }, { path: 'pharma.hospitalOnly', label: 'Restrição hospitalar', kind: 'bool' },
    { path: 'pharma.pbm', label: 'PBM', kind: 'bool' }, { path: 'pharma.farmaciaPopular', label: 'Farmácia Popular', kind: 'bool' },
    { path: 'pharma.continuousUse', label: 'Uso contínuo', kind: 'bool' },
    { path: 'pharma.priceRegime', label: 'Regime de preço', kind: 'select', options: [['', '—'], ['regulado', 'Regulado'], ['liberado', 'Liberado'], ['monitorado', 'Monitorado']] },
    { path: 'pharma.pisCofinsList', label: 'Lista PIS/COFINS', kind: 'select', options: [['', '—'], ['positiva', 'Positiva'], ['negativa', 'Negativa'], ['neutra', 'Neutra']] },
    { path: 'pharma.confaz87', label: 'CONFAZ 87', kind: 'bool' }, { path: 'pharma.icms0', label: 'ICMS 0%', kind: 'bool' },
    { path: 'pharma.ggrem', label: 'GGREM' }, { path: 'pharma.discontinuedAt', label: 'Descontinuado em (AAAA-MM-DD)' },
  ] },
  { title: 'Fotos e canais', fields: [
    { path: 'images', label: 'Fotos (uma URL https por linha; a primeira é a capa)', kind: 'lines', wide: true },
    { path: 'channels.mercadolivre.catalogProductId', label: 'Produto de catálogo MLB' }, { path: 'channels.mercadolivre.categoryId', label: 'Categoria Mercado Livre' },
    { path: 'channels.shopee.categoryId', label: 'Categoria Shopee' },
  ] },
];

const get = (o: any, path: string) => path.split('.').reduce((a, k) => (a == null ? a : a[k]), o);
const toForm = (v: unknown, kind: Kind = 'text'): string => {
  if (v == null) return '';
  if (kind === 'lines') return (v as string[]).join('\n');
  if (kind === 'ingredients') return (v as { name: string; strength: string | null }[]).map((a) => [a.name, a.strength].filter(Boolean).join(' | ')).join('\n');
  return String(v);
};
function fromForm(s: string, kind: Kind = 'text'): unknown {
  const t = s.trim();
  if (kind === 'lines') return t ? t.split(/\n+/).map((x) => x.trim()).filter(Boolean) : null;
  if (kind === 'ingredients') return t ? t.split(/\n+/).map((l) => { const [name, strength] = l.split('|').map((x) => x.trim()); return { name: name!, strength: strength || null }; }).filter((a) => a.name) : null;
  if (!t) return null;
  if (kind === 'number') return Number(t.replace(',', '.'));
  if (kind === 'bool') return t === 'true';
  return t;
}
/** Monta o corpo da API a partir dos campos alterados ("pharma.mip" → { pharma: { mip } }). */
function payloadOf(changed: Record<string, unknown>, current: any) {
  const out: any = {};
  for (const [path, v] of Object.entries(changed)) {
    const parts = path.split('.');
    let o = out;
    parts.slice(0, -1).forEach((k) => { o[k] ??= {}; o = o[k]; });
    o[parts.at(-1)!] = v;
  }
  // Conteúdo vai sempre com valor e unidade juntos.
  if (out.content) out.content = out.content.value === null && !out.content.unit ? null : { value: out.content.value ?? current.content?.value, unit: out.content.unit ?? current.content?.unit };
  if (out.content && (out.content.value == null || !out.content.unit)) delete out.content;
  if (out.fiscal && 'origin' in out.fiscal && out.fiscal.origin != null) out.fiscal.origin = Number(out.fiscal.origin);
  if (out.channels?.shopee && 'categoryId' in out.channels.shopee && out.channels.shopee.categoryId != null) out.channels.shopee.categoryId = String(out.channels.shopee.categoryId);
  return out;
}

export function AdminCatalogProduct() {
  const { id } = useParams();
  const isSuper = useMe().user.platformRole === 'superadmin';
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'catalog', 'product', id], queryFn: () => api<Master>(`/admin/catalog/products/${id}`) });
  const [form, setForm] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (body: unknown) => api<{ status: string; changed?: string[] }>(`/admin/catalog/products/${id}`, { method: 'PUT', json: body }),
    onSuccess: () => { setForm({}); qc.invalidateQueries({ queryKey: ['admin', 'catalog'] }); },
  });
  const p = q.data;
  const kindOf = (f: FieldDef) => f.kind ?? 'text';
  const changed = p ? Object.fromEntries(Object.entries(form).flatMap(([path, s]) => {
    const f = GROUPS.flatMap((g) => g.fields).find((x) => x.path === path)!;
    const now = toForm(get(p, path), kindOf(f));
    return s === now ? [] : [[path, fromForm(s, kindOf(f))]];
  })) : {};
  const nChanged = Object.keys(changed).length;

  if (q.error) return <ErrorBox error={q.error} />;
  if (!p) return <Loading />;
  const pharma = p.vertical === 'pharmacy' || !!p.pharma;
  const icms = [...new Set(p.prices.map((x) => x.icms))].sort((a, b) => a - b);

  return (
    <>
      <Head title={p.title}>
        <p><Link to="/admin/catalogo">← Catálogo mestre</Link> · EAN {p.gtins.join(', ') || '—'} · atualizada {dateTime(p.updatedAt)}</p>
      </Head>
      {p.missing.length > 0 && <div className="notice small" style={{ marginBottom: 'var(--gap)' }}><b>Falta:</b> {p.missing.map((m) => MISSING[m] ?? m).join(', ')}</div>}
      {save.error && <ErrorBox error={save.error} />}
      {save.data && <div className="notice small" style={{ marginBottom: 'var(--gap)' }}>Salvo: {save.data.changed?.join(', ') ?? 'nada mudou'}. Vale acima de qualquer fonte (a CMED e a ABCFarma não desfazem).</div>}

      <div className="stack-y">
        {GROUPS.filter((g) => !g.pharma || pharma).map((g) => (
          <section key={g.title} className="panel stack-y" style={{ gap: 12 }}>
            <h2 style={{ margin: 0 }}>{g.title}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
              {g.fields.map((f) => {
                const kind = kindOf(f);
                const value = form[f.path] ?? toForm(get(p, f.path), kind);
                const src = srcLabel(p.sources[f.path]);
                const setV = (v: string) => setForm((x) => ({ ...x, [f.path]: v }));
                const input: ReactNode = kind === 'area' || kind === 'lines' || kind === 'ingredients'
                  ? <textarea className="input" rows={kind === 'area' ? 4 : 3} value={value} disabled={!isSuper} onChange={(e) => setV(e.target.value)} />
                  : kind === 'bool' ? <select className="select" value={value} disabled={!isSuper} onChange={(e) => setV(e.target.value)}>{yesNo.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                  : kind === 'select' ? <select className="select" value={value} disabled={!isSuper} onChange={(e) => setV(e.target.value)}>{(f.options ?? []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}{value && !(f.options ?? []).some(([v]) => v === value) && <option value={value}>{value}</option>}</select>
                  : <input className="input" inputMode={kind === 'number' ? 'decimal' : undefined} value={value} disabled={!isSuper} onChange={(e) => setV(e.target.value)} />;
                return (
                  <label key={f.path} className="field" style={f.wide ? { gridColumn: '1 / -1' } : undefined}>
                    <span className="small" style={{ fontWeight: 600 }}>{f.label}</span>
                    {input}
                    <span className="small muted">{form[f.path] != null && f.path in changed ? <span style={{ color: 'var(--warn)' }}>alterado</span> : src ?? (value ? '' : 'vazio')}</span>
                  </label>
                );
              })}
            </div>
          </section>
        ))}

        {Object.keys(p.attributes ?? {}).length > 0 && (
          <section className="panel stack-y" style={{ gap: 8 }}>
            <h2 style={{ margin: 0 }}>Atributos</h2>
            <div className="small">{Object.entries(p.attributes).map(([k, v]) => <div key={k}><span className="code">{k}</span>: {String(v)} <span className="muted">{srcLabel(p.sources[`attributes.${k}`])}</span></div>)}</div>
          </section>
        )}

        <section className="panel stack-y" style={{ gap: 8 }}>
          <h2 style={{ margin: 0 }}>Preços (PF / PMC por alíquota de ICMS)</h2>
          {!p.prices.length ? <span className="small muted">Sem preço da CMED nem da ABCFarma.</span> : (
            <div style={{ overflowX: 'auto' }}>
              <table className="simple">
                <thead><tr><th>Fonte</th><th>Vigência</th>{icms.map((i) => <th key={i} className="num">{String(i).replace('.', ',')}%</th>)}</tr></thead>
                <tbody>
                  {(['cmed', 'abcfarma'] as const).flatMap((src) => (['pf', 'pmc'] as const).map((kind) => {
                    const rows = p.prices.filter((x) => x.source === src && x.kind === kind && !x.alc);
                    if (!rows.length) return null;
                    return (
                      <tr key={`${src}-${kind}`}>
                        <td><b>{SOURCE[src]}</b> {kind.toUpperCase()}</td>
                        <td className="small muted">{rows[0]!.validFrom ? new Date(`${rows[0]!.validFrom}T12:00:00`).toLocaleDateString('pt-BR') : '—'}</td>
                        {icms.map((i) => { const r = rows.find((x) => x.icms === i); return <td key={i} className="num">{r ? brl(r.value) : '—'}</td>; })}
                      </tr>
                    );
                  }))}
                </tbody>
              </table>
            </div>
          )}
          <span className="small muted">Preço com ALC (área de livre comércio) também está guardado; o cliente usa a alíquota do estado dele.</span>
        </section>

        <section className="panel stack-y" style={{ gap: 8 }}>
          <h2 style={{ margin: 0 }}>Clientes com esta ficha</h2>
          {!p.clients.length ? <span className="small muted">Nenhum cliente ligado a esta ficha ainda.</span> : (
            <div className="small">{p.clients.map((c, i) => <div key={i}><b>{c.tenant}</b> · SKU {c.sku}{c.title ? ` · ${c.title}` : ''}</div>)}</div>
          )}
        </section>
      </div>

      {isSuper && nChanged > 0 && (
        <div className="panel" style={{ position: 'sticky', bottom: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginTop: 'var(--gap)', zIndex: 2 }}>
          <span className="small muted" style={{ marginRight: 'auto' }}>{int(nChanged)} campo(s) alterado(s)</span>
          <button className="btn" type="button" onClick={() => setForm({})}>Descartar</button>
          <button className="btn primary" type="button" disabled={save.isPending} onClick={() => save.mutate(payloadOf(changed, p))}>Salvar ficha</button>
        </div>
      )}
    </>
  );
}

interface Suggestion {
  gtin: string; gtin14: string; title: string | null; categoryId: number; path: string | null; count: number; tenants: number; lastAt: string;
  current: { categoryId: number; path: string | null } | null;
}

/**
 * Categoria da Shopee no catálogo mestre: trocas manuais feitas nos clientes chegam como sugestão e só entram no mestre
 * (camada 1 da resolução de categoria) depois de aprovadas aqui.
 */
function ShopeeSuggestions({ isSuper }: { isSuper: boolean }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['admin', 'catalog', 'shopee-suggestions'], queryFn: () => api<Suggestion[]>('/admin/catalog/shopee-suggestions'), enabled: open });
  const review = useMutation({
    mutationFn: (v: { gtin14: string; categoryId: number; approve: boolean }) => api('/admin/catalog/shopee-suggestions/review', { method: 'POST', json: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'catalog', 'shopee-suggestions'] }),
  });
  return (
    <details className="panel" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)} style={{ marginBottom: 12 }}>
      <summary className="small"><b>Sugestões de categoria da Shopee</b> <span className="muted">(trocas manuais dos clientes; entram no mestre só depois de aprovadas)</span></summary>
      {!open ? null : list.isPending ? <Loading /> : list.error ? <ErrorBox error={list.error} /> : !list.data?.length ? (
        <p className="small muted">Nenhuma sugestão pendente.</p>
      ) : (
        <table className="simple" style={{ marginTop: 8 }}>
          <thead><tr><th>EAN</th><th>Produto</th><th>Categoria sugerida</th><th>No mestre hoje</th><th className="num">Sugestões</th><th /></tr></thead>
          <tbody>
            {list.data.map((s) => (
              <tr key={`${s.gtin14}-${s.categoryId}`}>
                <td className="code small">{s.gtin}</td>
                <td className="small">{s.title ?? '—'}</td>
                <td className="small">{s.path ?? s.categoryId}</td>
                <td className="small muted">{s.current ? s.current.path ?? s.current.categoryId : '—'}</td>
                <td className="num small">{int(s.count)} <span className="muted">({int(s.tenants)} cliente(s) · {dateTime(s.lastAt)})</span></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {isSuper && <>
                    <button className="btn sm primary" type="button" disabled={review.isPending} onClick={() => review.mutate({ gtin14: s.gtin14, categoryId: s.categoryId, approve: true })}>Aprovar</button>{' '}
                    <button className="btn sm quiet" type="button" disabled={review.isPending} onClick={() => review.mutate({ gtin14: s.gtin14, categoryId: s.categoryId, approve: false })}>Recusar</button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {review.error && <ErrorBox error={review.error} />}
    </details>
  );
}
